import mergeObjects from '@bybeam/platform-lib/answers/mergeObjects';
import {
  ApplicationConfig,
  ApplicationQuestion,
  ApplicationSection,
  CommonQuestionKeys,
  FieldType,
  KCCustomQuestionKeys,
  KCSMBIDACustomQuestionKeys,
  SMBGrantCustomQuestionKeys,
  TextInputType,
} from '@bybeam/platform-types';
import STANDARD_COMPONENTS from '../standards/index.js';
import { SpecialSectionKeys } from '../standards/types.js';
import { QUESTIONS as GRANT_QUESTIONS } from './questions/grantQuestions.js';
import { QUESTIONS as IDA_QUESTIONS } from './questions/idaQuestions.js';
import { QUESTIONS as SHARED_QUESTIONS } from './questions/sharedQuestions.js';

const SECTIONS: ApplicationSection[] = [
  {
    key: 'personalInfo',
    name: 'Owner Information',
    overview: {
      title: 'Owner Information',
      description:
        'The information below should be completed by the person who will be overseeing and setting up the bank account(s) with our banking partner. Must be the business owner and/or managing partner (per your state registration).',
    },
    questionGroups: [
      {
        key: 'personalInfo',
        name: 'About the Business Owner',
        questions: [
          SHARED_QUESTIONS[KCCustomQuestionKeys.BUSINESS_OWNER_NAME],
          SHARED_QUESTIONS[CommonQuestionKeys.EMAIL],
          SHARED_QUESTIONS[CommonQuestionKeys.PHONE_NUMBER],
          SHARED_QUESTIONS[KCCustomQuestionKeys.COMPANY_LEGAL_NAME],
          SHARED_QUESTIONS[KCCustomQuestionKeys.DOING_BUSINESS_AS],
          SHARED_QUESTIONS[KCCustomQuestionKeys.TITLE],
          SHARED_QUESTIONS[CommonQuestionKeys.ETHNICITY],
          SHARED_QUESTIONS[KCCustomQuestionKeys.PRODUCTS_SERVICES],
          SHARED_QUESTIONS[KCCustomQuestionKeys.EMPLOYEES],
          SHARED_QUESTIONS[KCCustomQuestionKeys.BUSINESS_ADDRESS],
          SHARED_QUESTIONS[KCCustomQuestionKeys.BUSINESS_LICENSE],
          SHARED_QUESTIONS[KCCustomQuestionKeys.TAX_CLEARANCE_LETTER],
          SHARED_QUESTIONS[KCCustomQuestionKeys.YEARS_IN_BUSINESS],
          SHARED_QUESTIONS[KCCustomQuestionKeys.BUSINESS_BANK_ACCOUNT],
          SHARED_QUESTIONS[CommonQuestionKeys.MAILING_ADDRESS],
          SHARED_QUESTIONS[CommonQuestionKeys.HOUSEHOLD_SIZE],
          SHARED_QUESTIONS[KCCustomQuestionKeys.DEPENDENTS],
          SHARED_QUESTIONS[CommonQuestionKeys.HOUSEHOLD_INCOME],
          SHARED_QUESTIONS[KCCustomQuestionKeys.NET_WORTH_EXCEEDS],
        ],
      },
    ],
  },
  {
    key: 'aboutTheBusiness',
    name: 'Business Pitch',
    overview: {
      description: '',
      title: 'Business Pitch',
    },
    questionGroups: [
      {
        key: 'aboutTheBusiness',
        name: 'Business Pitch',
        questions: [
          GRANT_QUESTIONS[SMBGrantCustomQuestionKeys.BUSINESS_DESCRIPTION],
          GRANT_QUESTIONS[SMBGrantCustomQuestionKeys.SOCIAL_IMPACT],
          GRANT_QUESTIONS[SMBGrantCustomQuestionKeys.TEAM_AND_EXPERTISE],
          GRANT_QUESTIONS[SMBGrantCustomQuestionKeys.PRIOR_FUNDING],
          GRANT_QUESTIONS[SMBGrantCustomQuestionKeys.MARKET_POTENTIAL],
        ],
      },
    ],
  },
  {
    key: 'financialInformation',
    name: 'Financial Information',
    overview: {
      title: 'Financial Information',
      description: '',
    },
    questionGroups: [
      {
        key: 'financialInformation',
        name: 'Financial Information',
        questions: [
          GRANT_QUESTIONS[SMBGrantCustomQuestionKeys.HAS_FINANCIAL_STATEMENTS],
          {
            ...GRANT_QUESTIONS[SMBGrantCustomQuestionKeys.YEARLY_SALES],
            fields: [
              {
                type: FieldType.Typography,
                copy: 'Please note that your financial records may be requested.',
                props: { variant: 'body', alignment: 'center' },
                key: 'yearlySales.helper',
              },
              {
                type: FieldType.Text,
                inputType: TextInputType.Currency,
                key: 'yearlySales.from2023',
                copy: '2023',
              },
              {
                type: FieldType.Text,
                inputType: TextInputType.Currency,
                key: 'yearlySales.from2022',
                copy: '2022',
              },
              {
                type: FieldType.Text,
                inputType: TextInputType.Currency,
                key: 'yearlySales.from2021',
                copy: '2021',
                validation: { required: false },
              },
              {
                type: FieldType.Text,
                inputType: TextInputType.Currency,
                key: 'yearlySales.from2020',
                copy: '2020',
                validation: { required: false },
              },
              {
                type: FieldType.Text,
                inputType: TextInputType.Currency,
                key: 'yearlySales.from2019',
                copy: '2019',
                validation: { required: false },
              },
              {
                key: 'yearlySales.yearlySales.label',
                type: FieldType.Typography,
                copy: 'Sales or Revenue (please indicate what you provided above)',
                props: { variant: 'body', alignment: 'center' },
              },
              {
                type: FieldType.Dropdown,
                options: [
                  { label: 'Revenue', value: 'revenue' },
                  { label: 'Sales', value: 'sales' },
                ],
                key: 'yearlySales.salesOrRevenue',
                copy: 'Please choose one',
              },
            ],
          },
          GRANT_QUESTIONS[SMBGrantCustomQuestionKeys.CASH_FLOW_POSITIVE],
          GRANT_QUESTIONS[SMBGrantCustomQuestionKeys.REVENUE_GOAL],
          GRANT_QUESTIONS[SMBGrantCustomQuestionKeys.CHALLENGES_AND_MITIGATION],
          GRANT_QUESTIONS[SMBGrantCustomQuestionKeys.BUSINESS_PLAN],
          GRANT_QUESTIONS[SMBGrantCustomQuestionKeys.BUSINESS_PLAN_DOCUMENTS],
        ],
      },
    ],
  },
  {
    key: 'attendance',
    name: 'Attendance and Savings Commitment',
    overview: {
      title: 'Attendance and Savings Commitment',
      description: '',
    },
    questionGroups: [
      {
        key: 'attendance',
        name: 'Attendance and Savings Commitment:',
        questions: [
          GRANT_QUESTIONS[SMBGrantCustomQuestionKeys.CAN_COMMIT_TO_SESSIONS],
          mergeObjects(
            [
              GRANT_QUESTIONS[SMBGrantCustomQuestionKeys.CAN_COMPLETE_MONTHLY_PROGRESS_REPORTS],
              {
                copy: {
                  title:
                    'Upon completing the Small Business Development Account Program, you will be required to complete progress reporting for up to 90 days. Do you acknowledge this requirement?',
                },
              } as ApplicationQuestion,
            ],
            'merge',
          ),
          GRANT_QUESTIONS[SMBGrantCustomQuestionKeys.ANY_CHALLENGES],
          IDA_QUESTIONS[KCSMBIDACustomQuestionKeys.CAN_COMMIT_TO_SAVING],
          IDA_QUESTIONS[KCSMBIDACustomQuestionKeys.SEE_ANY_CHALLENGES_MEETING_SAVINGS_COMMITMENTS],
        ],
      },
    ],
  },
  mergeObjects(
    [
      STANDARD_COMPONENTS.definitions.sections[SpecialSectionKeys.Review],
      {
        questionGroups: [
          // Drop the first question
          null,
          // Drop the first question and replace it with this one
          {
            questions: [null, IDA_QUESTIONS.affirmation],
          },
        ],
      } as ApplicationSection,
    ],
    'merge',
  ),
];

const CONFIG: ApplicationConfig = {
  introPages: [
    {
      key: 'what-is-it',
      layout: 'default',
      title: 'Small Business Development Account Program',
      body: 'The Small Business Development Account program is here to turn your entrepreneurial aspirations into a reality. An Individual Development Account (IDA) is a savings account where the money you save is matched by a program’s sponsor or financial institution. This groundbreaking initiative made possible by the City of Kansas City, Missouri, and the Ewing Marion Kauffman Foundation in partnership with the Economic Development Corporation of Kansas City, empowers local tourism entrepreneurs to reach new heights. The program provides matched savings funds, valuable business development resources, and crucial networking opportunities to help you and your business thrive.',
    },
    {
      key: 'how-it-works',
      layout: 'default',
      title: "Here's how it works:",
      body: `
  - **Save:** Commit to saving a minimum of $2,500 over a 4-month period in a dedicated IDA account.
  - **Match:** City funding provides a generous 4:1 match, potentially quadrupling your savings to a whopping $10,000! Bars and establishments that sell alcohol with less than 50% in food sales (sites with state and federal license for production and distribution may apply).
  - **Grow:** Access a wealth of workshops, mentorship, and technical assistance to hone your skills and build your business acumen.
  - **Connect:** Network with fellow entrepreneurs and industry leaders, forging valuable partnerships, and building a supportive community.
  `,
    },
    {
      key: 'requirements',
      layout: 'default',
      title:
        'Before you begin: Please review the program eligibility criteria below carefully to ensure your business qualifies:',
      body: `
  1. Must be a private, for-profit business.
  2. Must be operating in the tourism industries. (i.e. arts/entertainment, food, retail, etc.)
  3. Must employ ten (10) employees or less.
  4. Must have been in business for at least two (2) years, i.e. established prior to January 1, 2022.
  5. Must maintain an active 2024 KCMO business license (upload a copy).
  6. Must maintain a principal place of business within the boundaries of KCMO.
  7. Targeting entrepreneurs with annual household income at or below 80% of [the Area Median Income (AMI) for KCMO](https://dashboards.mysidewalk.com/kcmo-advancekc/income). Currently at $57,500 for a family of four. Not required to participate.
  8. Must commit to saving a minimum of $2,500 over a four-month period.
  `,
    },
    STANDARD_COMPONENTS.definitions.introPages.ApplicationProcess,
  ],
  overrides: {
    statuses: [
      {
        status: 'Application',
        label: '',
        referralMessage: 'You have been referred to this program.',
        message: `Capital assistance for small businesses 

Your business may be eligible for one of various forms of business development assistance, including educational and/or financial. Apply now!`,
      },
      {
        status: 'InProgress',
        label: '',
        referralMessage: 'You have been referred to this program.',
        message:
          'You’ve made great progress so far! Keep working on your application to receive business development assistance.',
      },
      {
        status: 'AwaitingReview',
        label: 'Status: Awaiting Review',
        message:
          'Thank you for submitting your application! You will be notified if our team needs additional information and/or of any next steps. Check back here for updates.',
      },
    ],
    submission: {
      title: 'What happens next?',
      bullets: [
        {
          key: 'review',
          title: 'Application review',
          icon: 'description',
          description:
            'While we look at your application, we might send you an email to ask for more information or notify you of next steps',
        },
      ],
    },
  },
  sections: SECTIONS,
};

export default CONFIG;
